import { QUOTES_CODE_MAPPING } from '@fe-common-utils/libs/quote';
import { getStockCode } from '@fe-common-utils/libs/stock';
import { GETv1StockRanking } from '@fe-news/api/quotes';
import queries from '@fe-news/components/ReactQuery';
import { useQuery } from '@tanstack/react-query';

export type SortType = 'up' | 'down';

type StockRankingDataType = {
  id: number;
  name: string;
  performance: string;
  stocks: Quote[];
};

export type RankingStockType = {
  index: string;
  code: string;
  name: string;
  price: string;
  performance: string;
};

export type RankingSubjectType = {
  id: number;
  title: string;
  ratio: string;
  color: string;
};

const successStatusCode = 200;
const emptyValue = '--';
const defaultValue = {
  id: Math.floor(Math.random() * 10_000),
  name: emptyValue,
  performance: emptyValue,
  stocks: Array.from({ length: 5 }).fill({
    0: emptyValue,
    200_009: emptyValue,
    220_026: emptyValue,
    performance: emptyValue
  })
};

const orderIcons = [
  {
    icon: '/assets/icons/stocksRanking/index-1.svg',
    background: 'linear-gradient(53deg, #f5c702 0%, #ffe908)'
  },
  {
    icon: '/assets/icons/stocksRanking/index-2.svg',
    background: 'linear-gradient(53deg, #a5b1c6 0%, #c0cde4)'
  },
  {
    icon: '/assets/icons/stocksRanking/index-3.svg',
    background: 'linear-gradient(53deg, #e76e00, #ffb25d)'
  },
  {
    icon: '/assets/icons/stocksRanking/index-4.svg',
    background: 'linear-gradient(34deg, #515c69, #8698ae)'
  },
  {
    icon: '/assets/icons/stocksRanking/index-5.svg',
    background: 'linear-gradient(34deg, #515c69, #8698ae)'
  }
];

export const fetchConceptStocksRanking = async ({
  period = 0,
  sort = 'up',
  country = 'tws',
  theme = 0
}: {
  period: number;
  sort: SortType;
  country: string;
  theme: number;
}) => {
  try {
    const response = await GETv1StockRanking({
      period,
      sort,
      country,
      theme
    });

    if (response.statusCode === successStatusCode) {
      return response?.data ?? [];
    }

    return Array.from({ length: 5 }).fill(defaultValue);
  } catch (error) {
    console.log('HotStocks fetch error', error);
    return Array.from({ length: 5 }).fill(defaultValue);
  }
};

const formateData = (data: StockRankingDataType[]) => {
  const allStocks: RankingStockType[][] = [];
  const subjects: RankingSubjectType[] = [];

  data.forEach(({ id, name, performance, stocks: stockList }: StockRankingDataType, index: number) => {
    subjects.push({
      id,
      title: name,
      ratio: performance,
      color: orderIcons[index]?.background
    });

    const stocks = stockList.map((quote: Quote, orderIndex) => {
      const stockName = (quote?.[QUOTES_CODE_MAPPING.ChineseName as keyof Quote] as string) ?? emptyValue;
      const code = getStockCode(quote?.[QUOTES_CODE_MAPPING.Symbol as keyof Quote] as string) as string;
      const price = (quote?.[QUOTES_CODE_MAPPING.lastFormat as keyof Quote] as string) ?? emptyValue;
      const changeRatio = quote?.performance ?? emptyValue;

      return {
        index: orderIcons[orderIndex]?.icon,
        code,
        name: stockName,
        price,
        performance: changeRatio
      };
    });

    allStocks.push(stocks);
  });

  return { stocks: allStocks, subjects };
};

const useFetchConceptStocksRanking = ({
  period,
  sort,
  country,
  theme
}: {
  period: number;
  sort: SortType;
  country: string;
  theme: number;
}) => {
  const { data } = useQuery({
    ...queries.quotes.QuotesByConceptStocksRanking({
      period,
      sort,
      country,
      theme
    }),
    select: formateData,
    initialData: []
  });

  return { data };
};

export default useFetchConceptStocksRanking;
